import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUploadGuard } from './UploadGuardContext';
import { createPrivacyReview, detectPrivacySignals, PRIVACY_CHECK_ITEMS } from './privacyReview';
import { COMMON_TAGS } from './tagVocabulary';
import StepIndicator from './StepIndicator';
import { track } from './analytics';
import './UploadSteps.css';

const normalizeDocumentType = (documentType) => {
    const value = String(documentType || '').trim().toLowerCase();

    if (value === 'estimate' || value === 'estimated' || value === 'treatment plan') {
        return 'estimate';
    }

    return 'bill';
};

const MIN_REDACTION_SIZE = 8;
// A tap (rather than a drag) moves less than this many screen pixels.
const TAP_MOVEMENT_THRESHOLD = 6;

const isTouchDevice = () =>
    typeof window !== 'undefined' &&
    ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0);

const Step3Details = ({ setOcrData }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [ocrData] = useState(location.state?.ocrData || {});
    const [tags, setTags] = useState(location.state?.tags || []);
    const [newTag, setNewTag] = useState('');
    const [privacyChecklist, setPrivacyChecklist] = useState(location.state?.ocrData?.privacyReview?.checkedItems || {});
    const [privacyNotes, setPrivacyNotes] = useState(location.state?.ocrData?.privacyReview?.userNotes || '');
    const [redactionBoxes, setRedactionBoxes] = useState(location.state?.ocrData?.privacyReview?.redactionBoxes || []);
    const [draftRedactionBox, setDraftRedactionBox] = useState(null);
    const [tapToRedact, setTapToRedact] = useState(isTouchDevice());
    const [autoRedactApplied, setAutoRedactApplied] = useState(false);
    const [autoRedactCount, setAutoRedactCount] = useState(0);
    const gestureRef = useRef(null);
    // Normalized [0,1] PII rectangles detected during extraction (page 0).
    const piiRegions = Array.isArray(location.state?.ocrData?.piiRegions)
        ? location.state.ocrData.piiRegions
        : [];
    const [redactionExpanded, setRedactionExpanded] = useState(false);
    const [activeRedactionPage, setActiveRedactionPage] = useState(0);
    const [showSaveWithoutRedactionWarning, setShowSaveWithoutRedactionWarning] = useState(false);
    const [saving, setSaving] = useState(false);
    const [visibility, setVisibility] = useState(location.state?.ocrData?.visibility || 'public');
    const [pets, setPets] = useState([]);
    const [selectedPetId, setSelectedPetId] = useState('');
    const imageRef = useRef(null);
    const redactionSurfaceRef = useRef(null);
    const { markUploadInProgress, clearUploadInProgress } = useUploadGuard();

    const vetHospitalName = ocrData.vetHospital?.name || ocrData.vetHospital || 'Unknown hospital';
    const cityRegion = ocrData.cityRegion || [ocrData.vetHospital?.city, ocrData.vetHospital?.state].filter(Boolean).join(', ');
    const normalizedDocumentType = normalizeDocumentType(ocrData.documentType);
    const documentLabel = normalizedDocumentType;
    const services = Array.isArray(ocrData.services) ? ocrData.services : [];
    const documentImageUrl = ocrData.uploadedFileUrl || (ocrData.uploadedFileName ? `/uploads/${ocrData.uploadedFileName}` : '');
    const pageUrls = Array.isArray(ocrData.pageUrls) && ocrData.pageUrls.length > 0
        ? ocrData.pageUrls
        : (documentImageUrl ? [documentImageUrl] : []);
    const pageFileNames = Array.isArray(ocrData.pageFileNames) && ocrData.pageFileNames.length > 0
        ? ocrData.pageFileNames
        : (ocrData.uploadedFileName ? [ocrData.uploadedFileName] : []);
    const isMultiPage = pageUrls.length > 1;
    const detectedPrivacySignals = detectPrivacySignals(ocrData.rawOcrText);
    const privacyChecklistComplete = PRIVACY_CHECK_ITEMS.every(item => privacyChecklist[item.id]);
    // The required PII checklist IS the review affirmation now — completing it
    // marks the upload reviewed. (The separate final confirmation checkbox was
    // redundant with these items.)
    const privacyReviewed = privacyChecklistComplete;
    const suggestedTags = Array.isArray(ocrData.suggestedTags) ? ocrData.suggestedTags : [];
    const availableSuggestedTags = suggestedTags.filter(suggestedTag =>
        !tags.some(tag => tag.toLowerCase() === suggestedTag.toLowerCase())
    );
    // Curated common tags not already applied — the predictable, one-tap set.
    const availableCommonTags = COMMON_TAGS.filter(commonTag =>
        !tags.some(tag => tag.toLowerCase() === commonTag)
    );
    const serviceGroups = services.reduce((groups, service) => {
        const category = service.standardization?.category || 'Other';
        if (!groups[category]) {
            groups[category] = [];
        }
        groups[category].push(service.description || 'Unnamed service');
        return groups;
    }, {});
    const serviceGroupEntries = Object.entries(serviceGroups).sort((a, b) => b[1].length - a[1].length);

    const handleTagChange = (e) => {
        setNewTag(e.target.value);
    };

    const handleAddTag = () => {
        const cleanedTag = newTag.trim().toLowerCase();
        if (cleanedTag && !tags.some(tag => tag.toLowerCase() === cleanedTag.toLowerCase())) {
            setTags([...tags, cleanedTag]);
            setNewTag('');
        }
    };

    const handleAddSuggestedTag = (tagToAdd) => {
        const cleaned = String(tagToAdd).toLowerCase();
        if (!tags.some(tag => tag.toLowerCase() === cleaned)) {
            setTags([...tags, cleaned]);
        }
    };

    const handleDeleteTag = (tagToDelete) => {
        setTags(tags.filter(tag => tag !== tagToDelete));
    };

    const handlePrivacyChecklistChange = (itemId) => {
        setPrivacyChecklist(current => ({
            ...current,
            [itemId]: !current[itemId]
        }));
    };

    const getRedactionPoint = (event) => {
        const image = imageRef.current;
        if (!image || !image.naturalWidth || !image.naturalHeight) {
            return null;
        }

        const rect = image.getBoundingClientRect();
        const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
        const y = Math.max(0, Math.min(event.clientY - rect.top, rect.height));

        return {
            x: (x / rect.width) * image.naturalWidth,
            y: (y / rect.height) * image.naturalHeight
        };
    };

    const getDisplayBox = (box) => {
        const image = imageRef.current;
        if (!image || !image.naturalWidth || !image.naturalHeight) {
            return null;
        }

        return {
            left: `${(box.x / image.naturalWidth) * 100}%`,
            top: `${(box.y / image.naturalHeight) * 100}%`,
            width: `${(box.width / image.naturalWidth) * 100}%`,
            height: `${(box.height / image.naturalHeight) * 100}%`
        };
    };

    const normalizeBox = (start, end) => ({
        x: Math.min(start.x, end.x),
        y: Math.min(start.y, end.y),
        width: Math.abs(end.x - start.x),
        height: Math.abs(end.y - start.y)
    });

    // A single tap drops a redaction band sized to cover a typical line of
    // text, centered on the tap — far easier on a phone than dragging.
    const buildTapBand = (point) => {
        const image = imageRef.current;
        if (!image) {
            return null;
        }
        const bandWidth = image.naturalWidth * 0.5;
        const bandHeight = Math.max(image.naturalHeight * 0.045, 24);
        const x = Math.max(0, Math.min(point.x - bandWidth / 2, image.naturalWidth - bandWidth));
        const y = Math.max(0, Math.min(point.y - bandHeight / 2, image.naturalHeight - bandHeight));
        return { x, y, width: bandWidth, height: bandHeight };
    };

    const handleRedactionPointerDown = (event) => {
        const point = getRedactionPoint(event);
        if (!point) {
            return;
        }

        event.currentTarget.setPointerCapture?.(event.pointerId);
        gestureRef.current = { startClientX: event.clientX, startClientY: event.clientY, moved: false };
        setDraftRedactionBox({ start: point, end: point });
    };

    const handleRedactionPointerMove = (event) => {
        if (!draftRedactionBox) {
            return;
        }

        if (gestureRef.current) {
            const dx = Math.abs(event.clientX - gestureRef.current.startClientX);
            const dy = Math.abs(event.clientY - gestureRef.current.startClientY);
            if (dx > TAP_MOVEMENT_THRESHOLD || dy > TAP_MOVEMENT_THRESHOLD) {
                gestureRef.current.moved = true;
            }
        }

        const point = getRedactionPoint(event);
        if (point) {
            setDraftRedactionBox(current => ({ ...current, end: point }));
        }
    };

    const finishRedactionBox = () => {
        if (!draftRedactionBox) {
            return;
        }

        const gesture = gestureRef.current;
        gestureRef.current = null;
        const draggedBox = normalizeBox(draftRedactionBox.start, draftRedactionBox.end);
        const wasTap = !gesture || !gesture.moved;

        let nextBox = null;
        if (wasTap && tapToRedact) {
            nextBox = buildTapBand(draftRedactionBox.start);
        } else if (draggedBox.width >= MIN_REDACTION_SIZE && draggedBox.height >= MIN_REDACTION_SIZE) {
            nextBox = draggedBox;
        }

        if (nextBox) {
            setRedactionBoxes(current => [...current, { ...nextBox, page: activeRedactionPage }]);
        }

        setDraftRedactionBox(null);
    };

    const removeRedactionBox = (boxToRemove) => {
        setRedactionBoxes(current => current.filter(box => box !== boxToRemove));
    };

    // Pre-fill redaction boxes over detected personal information the first time
    // the document image loads, so the user starts with likely PII covered and
    // only has to review/adjust. Runs once, and never overwrites existing boxes.
    const applyAutoRedactions = () => {
        if (autoRedactApplied) {
            return;
        }
        const image = imageRef.current;
        if (!image || !image.naturalWidth || !image.naturalHeight) {
            return;
        }
        setAutoRedactApplied(true);
        if (!piiRegions.length || redactionBoxes.length > 0) {
            return;
        }
        const boxes = piiRegions.map(region => ({
            x: region.x * image.naturalWidth,
            y: region.y * image.naturalHeight,
            width: region.width * image.naturalWidth,
            height: region.height * image.naturalHeight,
            page: 0,
            auto: true
        }));
        setRedactionBoxes(boxes);
        setAutoRedactCount(boxes.length);
    };

    const boxesForPage = (pageIndex) => redactionBoxes.filter(box => (box.page || 0) === pageIndex);
    const activePageBoxes = boxesForPage(activeRedactionPage);

    const handleUndoRedaction = () => {
        setRedactionBoxes(current => {
            const lastOnPage = current.map((box, i) => ({ box, i }))
                .filter(({ box }) => (box.page || 0) === activeRedactionPage)
                .pop();
            return lastOnPage ? current.filter((_, i) => i !== lastOnPage.i) : current;
        });
    };

    const handleClearRedactions = () => {
        setRedactionBoxes(current => current.filter(box => (box.page || 0) !== activeRedactionPage));
    };

    const openRedactionExpanded = () => {
        setRedactionExpanded(true);
        setDraftRedactionBox(null);
    };

    const closeRedactionExpanded = () => {
        setRedactionExpanded(false);
        setDraftRedactionBox(null);
    };

    const loadPageImage = (src) => new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Could not load the page image for redaction.'));
        image.src = src;
    });

    const createRedactedFileForPage = async (pageIndex) => {
        const boxes = boxesForPage(pageIndex);
        const image = await loadPageImage(pageUrls[pageIndex]);

        if (!image.naturalWidth || !image.naturalHeight) {
            throw new Error('The uploaded image is not ready for redaction.');
        }

        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;

        const context = canvas.getContext('2d');
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        context.fillStyle = '#111827';
        boxes.forEach(box => {
            context.fillRect(box.x, box.y, box.width, box.height);
        });

        return new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('Could not create the redacted image.'));
                    return;
                }
                resolve(new File([blob], `redacted-p${pageIndex + 1}-${Date.now()}.png`, { type: 'image/png' }));
            }, 'image/png');
        });
    };

    const cleanData = (data) => {
        if (!Array.isArray(data)) {
            return [];
        }

        return data.map(service => {
            return {
                ...service,
                unitCost: typeof service.unitCost === 'string' ? service.unitCost.replace(/,/g, '') : service.unitCost,
                totalCost: typeof service.totalCost === 'string' ? service.totalCost.replace(/,/g, '') : service.totalCost
            };
        });
    };

    const handleSubmit = async (e, options = {}) => {
        e.preventDefault();
        if (!ocrData.uploadedFileName) {
            alert('No processed upload found. Please upload the document again.');
            return;
        }

        if (!privacyReviewed || !privacyChecklistComplete) {
            alert('Please complete the personal information review before saving.');
            return;
        }

        if (detectedPrivacySignals.length > 0 && redactionBoxes.length === 0 && !options.confirmedSaveWithoutRedactions) {
            setShowSaveWithoutRedactionWarning(true);
            return;
        }

        // Compositing redacted pages + uploading can take a moment (especially
        // multi-page), so show a blocking progress indicator.
        setSaving(true);
        track('save_started', { serviceCount: services.length, documentType: normalizedDocumentType });
        const formData = new FormData();

        const cleanedServices = cleanData(services);

        if (isMultiPage) {
            // Multi-page: send a page manifest. Pages with redaction boxes are
            // replaced by composited images; untouched pages reuse server files.
            try {
                const manifest = [];
                for (let i = 0; i < pageUrls.length; i += 1) {
                    if (boxesForPage(i).length > 0) {
                        const redactedFile = await createRedactedFileForPage(i);
                        formData.append('pageFiles', redactedFile);
                        manifest.push({ source: 'upload', replaces: pageFileNames[i] || '' });
                    } else {
                        manifest.push({ source: 'existing', filename: pageFileNames[i] });
                    }
                }
                formData.append('pageManifest', JSON.stringify(manifest));
            } catch (error) {
                setSaving(false);
                alert(error.message);
                return;
            }
        } else if (redactionBoxes.length > 0) {
            try {
                const redactedFile = await createRedactedFileForPage(0);
                formData.append('file', redactedFile);
                formData.append('sourceFilename', ocrData.uploadedFileName);
            } catch (error) {
                setSaving(false);
                alert(error.message);
                return;
            }
        } else {
            formData.append('existingFilename', ocrData.uploadedFileName);
        }

        formData.append('vetHospital', JSON.stringify(ocrData.vetHospital || {}));
        formData.append('documentType', normalizedDocumentType);
        formData.append('cityRegion', cityRegion);
        formData.append('tags', JSON.stringify(tags));
        formData.append('billDate', ocrData.billDate || '');
        formData.append('services', JSON.stringify(cleanedServices || []));
        formData.append('privacyReviewed', String(privacyReviewed));
        formData.append('privacyReview', JSON.stringify(createPrivacyReview({
            checkedItems: privacyChecklist,
            detectedSignals: detectedPrivacySignals,
            userNotes: privacyNotes,
            redactionApplied: redactionBoxes.length > 0,
            redactionBoxCount: redactionBoxes.length
        })));
        formData.append('redactionApplied', String(redactionBoxes.length > 0));
        formData.append('redactionBoxCount', String(redactionBoxes.length));
        formData.append('visibility', visibility);
        if (selectedPetId) {
            formData.append('petId', selectedPetId);
        }

        try {
            const response = await fetch('/upload', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}` // Adjust if using token-based auth
                },
                body: formData
            });

            const data = await response.json();
            if (data.message === 'Upload successful') {
                track('save_succeeded', { serviceCount: services.length, documentType: normalizedDocumentType });
                clearUploadInProgress();
                const uploadDetails = {
                    vetHospital: vetHospitalName,
                    documentType: normalizedDocumentType,
                    cityRegion,
                    tags,
                    billDate: ocrData.billDate,
                    privacyReviewed,
                    privacyReview: createPrivacyReview({
                        checkedItems: privacyChecklist,
                        detectedSignals: detectedPrivacySignals,
                        userNotes: privacyNotes,
                        redactionApplied: redactionBoxes.length > 0,
                        redactionBoxCount: redactionBoxes.length
                    }),
                    imageUrl: data.imageUrl,
                    billId: data.billId,
                    petId: data.petId || null,
                    visibility
                };
                navigate('/upload-success', {
                    state: {
                        lastUpload: uploadDetails,
                        pets,
                        extractedPatient: ocrData.patient || null
                    }
                });
            } else {
                setSaving(false);
                alert('Error saving upload: ' + data.message);
            }
        } catch (error) {
            setSaving(false);
            console.error('Error saving upload:', error);
            alert('Error saving upload. Please try again later.');
        }
    };

    const handleBack = () => {
        navigate('/upload/step2', {
            state: {
                ocrData: {
                    ...ocrData,
                    tags,
                    privacyReviewed,
                    privacyReview: createPrivacyReview({
                        checkedItems: privacyChecklist,
                        detectedSignals: detectedPrivacySignals,
                        userNotes: privacyNotes,
                        redactionApplied: redactionBoxes.length > 0,
                        redactionBoxCount: redactionBoxes.length
                    })
                },
                tags
            }
        });
    };

    const handleGoBackAndRedact = () => {
        setShowSaveWithoutRedactionWarning(false);
        openRedactionExpanded();
    };

    const handleConfirmSaveWithoutRedactions = (event) => {
        setShowSaveWithoutRedactionWarning(false);
        handleSubmit(event, { confirmedSaveWithoutRedactions: true });
    };

    React.useEffect(() => {
        if (ocrData.uploadedFileName) {
            markUploadInProgress();
        }
    }, [ocrData.uploadedFileName, markUploadInProgress]);

    // Fire once when the last step is reached, for the funnel.
    useEffect(() => {
        track('details_viewed', { serviceCount: services.length });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Existing pet profiles: offer a one-tap optional attachment. When the
    // user has exactly one pet, default to it (still changeable).
    useEffect(() => {
        let cancelled = false;
        fetch('/api/pets', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
            .then(response => (response.ok ? response.json() : []))
            .then(data => {
                if (cancelled || !Array.isArray(data)) return;
                setPets(data);
                if (data.length === 1) {
                    setSelectedPetId(String(data[0]._id));
                }
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    return (
        <div className="upload-steps-container details-container">
            <StepIndicator currentStep={3} />
            <h2>Last step before saving</h2>
            <div className="final-review-summary">
                <div>
                    <span>Hospital</span>
                    <strong>{vetHospitalName}</strong>
                </div>
                <div>
                    <span>Location</span>
                    <strong>{cityRegion || 'Not listed'}</strong>
                </div>
                <div>
                    <span>Type</span>
                    <strong>{normalizedDocumentType}</strong>
                </div>
                <div>
                    <span>Services</span>
                    <strong>{services.length}</strong>
                </div>
            </div>

            <div className="details-content-grid">
                <div className="review-card tag-review-card">
                    <div className="card-heading-row">
                        <div>
                            <h3>Tags</h3>
                            <p className="muted-text">Tags help others find this when they search. Tap a common tag to add it, or type your own.</p>
                        </div>
                        <span className="compact-count">{tags.length} tags</span>
                    </div>
                    <div className="review-tag-list applied-tag-list">
                        {tags.length ? tags.map((tag, index) => (
                            <span key={index} className="review-tag">
                                {tag}
                                <button type="button" onClick={() => handleDeleteTag(tag)}>x</button>
                            </span>
                        )) : <span className="muted-text">No tags added.</span>}
                    </div>
                    {availableSuggestedTags.length > 0 && (
                        <div className="suggested-tags-panel compact-suggestions">
                            <span>Suggested for this bill</span>
                            <div className="review-tag-list">
                                {availableSuggestedTags.map(tag => (
                                    <button
                                        key={tag}
                                        type="button"
                                        className="suggested-tag"
                                        onClick={() => handleAddSuggestedTag(tag)}
                                    >
                                        {tag}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {availableCommonTags.length > 0 && (
                        <div className="suggested-tags-panel compact-suggestions">
                            <span>Common tags — tap to add</span>
                            <div className="review-tag-list">
                                {availableCommonTags.map(tag => (
                                    <button
                                        key={tag}
                                        type="button"
                                        className="suggested-tag"
                                        onClick={() => handleAddSuggestedTag(tag)}
                                    >
                                        {tag}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="tag-editor tag-editor-custom">
                        <input type="text" value={newTag} onChange={handleTagChange} placeholder="Add your own tag…" />
                        <button type="button" onClick={handleAddTag}>Add</button>
                    </div>
                </div>

                <div className="review-card services-review-card">
                    <div className="card-heading-row">
                        <div>
                            <h3>Service Summary</h3>
                            <p className="muted-text">These categories help people compare this {documentLabel} with similar uploads.</p>
                        </div>
                        <span className="compact-count">{services.length} lines</span>
                    </div>
                    <div className="service-summary-list">
                        {serviceGroupEntries.map(([category, descriptions]) => (
                            <div key={category} className="service-summary-row">
                                <div>
                                    <strong>{category}</strong>
                                    <span>{descriptions.slice(0, 2).join(', ')}</span>
                                </div>
                                <span className="service-summary-count">{descriptions.length}</span>
                            </div>
                        ))}
                        {!serviceGroupEntries.length && <span className="muted-text">No services were reviewed.</span>}
                    </div>
                </div>
            </div>

            <div className="review-card privacy-confirmation-card">
                <div className="card-heading-row">
                    <div>
                        <h3>Check for personal info</h3>
                        <p className="muted-text">Look over the image and hide anything you don’t want other people to see.</p>
                    </div>
                    <span className={`privacy-status-pill ${privacyChecklistComplete ? 'complete' : ''}`}>
                        {privacyChecklistComplete ? 'Reviewed' : `${PRIVACY_CHECK_ITEMS.filter(item => privacyChecklist[item.id]).length}/${PRIVACY_CHECK_ITEMS.length}`}
                    </span>
                </div>

                {detectedPrivacySignals.length > 0 ? (
                    <div className="privacy-risk-panel">
                        <strong>We spotted some possible personal info</strong>
                        <div>
                            {detectedPrivacySignals.map(signal => (
                                <span key={signal.id}>{signal.label}</span>
                            ))}
                        </div>
                    </div>
                ) : autoRedactCount > 0 ? null : (
                    <div className="privacy-risk-panel low-risk">
                        <strong>We didn’t spot anything obviously personal</strong>
                        <p>Still give the image a look — automatic reading can miss things, especially on photos and scans.</p>
                    </div>
                )}

                <div className="privacy-review-grid">
                    {documentImageUrl && (
                        <div className={`redaction-tool ${redactionExpanded ? 'expanded' : ''}`}>
                            <div className="redaction-tool-header">
                                <div>
                                    <h4>Hide personal info</h4>
                                    <p>
                                        {tapToRedact
                                            ? 'Tap anything personal — name, address, phone, account number — to cover it. Tap a box’s ✕ to undo.'
                                            : (redactionExpanded ? 'Drag a box over anything you don’t want public.' : 'Open the bigger view to drag boxes over anything you don’t want public.')}
                                    </p>
                                </div>
                                <span>{redactionBoxes.length} hidden{isMultiPage ? ` (${activePageBoxes.length} on this page)` : ''}</span>
                            </div>
                            <div className="redaction-mode" role="group" aria-label="How to hide info">
                                <button type="button" className={tapToRedact ? 'active' : ''} onClick={() => setTapToRedact(true)} aria-pressed={tapToRedact}>Tap to hide</button>
                                <button type="button" className={!tapToRedact ? 'active' : ''} onClick={() => setTapToRedact(false)} aria-pressed={!tapToRedact}>Draw a box</button>
                            </div>
                            {autoRedactCount > 0 && (
                                <p className="redaction-auto-note">
                                    We auto-covered {autoRedactCount} spot{autoRedactCount === 1 ? '' : 's'} — double-check them and cover anything we missed.
                                </p>
                            )}
                            {isMultiPage && (
                                <div className="page-pager redaction-pager">
                                    <button type="button" onClick={() => setActiveRedactionPage(p => Math.max(0, p - 1))} disabled={activeRedactionPage === 0} aria-label="Previous page">‹</button>
                                    <span>Page {activeRedactionPage + 1} of {pageUrls.length} — check every page</span>
                                    <button type="button" onClick={() => setActiveRedactionPage(p => Math.min(pageUrls.length - 1, p + 1))} disabled={activeRedactionPage === pageUrls.length - 1} aria-label="Next page">›</button>
                                </div>
                            )}
                            <div
                                className="redaction-canvas"
                            >
                                <div
                                    className="redaction-image-frame"
                                    ref={redactionSurfaceRef}
                                    onPointerDown={handleRedactionPointerDown}
                                    onPointerMove={handleRedactionPointerMove}
                                    onPointerUp={finishRedactionBox}
                                    onPointerCancel={finishRedactionBox}
                                >
                                    <img ref={imageRef} src={pageUrls[activeRedactionPage] || documentImageUrl} alt={`Uploaded ${documentLabel} for redaction${isMultiPage ? `, page ${activeRedactionPage + 1}` : ''}`} draggable="false" onLoad={applyAutoRedactions} />
                                    {activePageBoxes.map((box, index) => {
                                        const displayBox = getDisplayBox(box);
                                        if (!displayBox) {
                                            return null;
                                        }
                                        return (
                                            <span key={index} className="redaction-box" style={displayBox}>
                                                <button
                                                    type="button"
                                                    className="redaction-box-remove"
                                                    onPointerDown={(e) => { e.stopPropagation(); }}
                                                    onClick={(e) => { e.stopPropagation(); removeRedactionBox(box); }}
                                                    aria-label={`Remove redaction box ${index + 1}`}
                                                >
                                                    ✕
                                                </button>
                                            </span>
                                        );
                                    })}
                                    {draftRedactionBox && (() => {
                                        const displayBox = getDisplayBox(normalizeBox(draftRedactionBox.start, draftRedactionBox.end));
                                        return displayBox ? <span className="redaction-box draft" style={displayBox} /> : null;
                                    })()}
                                </div>
                            </div>
                            <div className="redaction-actions">
                                {!redactionExpanded && (
                                    <button type="button" className="next-btn redaction-expand-btn" onClick={openRedactionExpanded}>Open bigger view</button>
                                )}
                                <button type="button" className="secondary-btn" onClick={handleUndoRedaction} disabled={!activePageBoxes.length}>Undo</button>
                                <button type="button" className="secondary-btn" onClick={handleClearRedactions} disabled={!activePageBoxes.length}>{isMultiPage ? 'Clear this page' : 'Clear all'}</button>
                                {redactionExpanded && (
                                    <button type="button" className="secondary-btn" onClick={closeRedactionExpanded}>Done</button>
                                )}
                            </div>
                        </div>
                    )}

                    <fieldset className={`privacy-required-box ${privacyChecklistComplete ? 'complete' : ''}`}>
                        <legend>Required before saving</legend>
                        <p className="privacy-required-hint">Check each once you’ve hidden it — or you’re sure it isn’t on the document. All four are required to save.</p>
                        <div className="privacy-checklist">
                            {PRIVACY_CHECK_ITEMS.map(item => (
                                <label key={item.id}>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(privacyChecklist[item.id])}
                                        onChange={() => handlePrivacyChecklistChange(item.id)}
                                    />
                                    <span>
                                        <strong>{item.label}</strong>
                                        <small>{item.helper}</small>
                                    </span>
                                </label>
                            ))}
                        </div>
                    </fieldset>
                </div>

                <label className="privacy-notes-field">
                    Optional notes for yourself
                    <textarea
                        value={privacyNotes}
                        onChange={event => setPrivacyNotes(event.target.value)}
                        placeholder="Example: cropped owner phone number before uploading"
                        rows="3"
                    />
                </label>

                {pets.length > 0 && (
                    <label className="pet-attach-field">
                        <span className="field-label">Pet (optional)</span>
                        <select value={selectedPetId} onChange={e => setSelectedPetId(e.target.value)}>
                            <option value="">Not attached to a pet</option>
                            {pets.map(pet => (
                                <option key={pet._id} value={pet._id}>
                                    {pet.name}{pet.species ? ` (${pet.species})` : ''}
                                </option>
                            ))}
                        </select>
                        <small>Attaching builds your pet's private cost history. Never shown publicly.</small>
                    </label>
                )}

                <fieldset className="visibility-choice">
                    <legend>Who can see this upload?</legend>
                    <label className={visibility === 'public' ? 'selected' : ''}>
                        <input
                            type="radio"
                            name="visibility"
                            value="public"
                            checked={visibility === 'public'}
                            onChange={() => setVisibility('public')}
                        />
                        <span>
                            <strong>Share publicly</strong>
                            <small>Adds the prices to VetBill's comparison data so other pet owners can benefit. Your name and account are never shown.</small>
                        </span>
                    </label>
                    <label className={visibility === 'private' ? 'selected' : ''}>
                        <input
                            type="radio"
                            name="visibility"
                            value="private"
                            checked={visibility === 'private'}
                            onChange={() => setVisibility('private')}
                        />
                        <span>
                            <strong>Keep private</strong>
                            <small>Only you can see it — for tracking your own pet's care and costs. You can make it public later from your dashboard.</small>
                        </span>
                    </label>
                </fieldset>

            </div>

            <div className="review-actions">
                <button type="button" onClick={handleBack} className="secondary-btn" disabled={saving}>Back</button>
                <button type="submit" onClick={handleSubmit} className="next-btn" disabled={saving || !privacyChecklistComplete}>
                    {saving ? 'Saving…' : (visibility === 'private' ? 'Save to my records' : 'Save and help others compare')}
                </button>
            </div>

            {saving && (
                <div className="processing-overlay" role="status" aria-live="polite">
                    <div className="processing-card">
                        <div className="processing-spinner" aria-hidden="true" />
                        <strong>Saving your {documentLabel}</strong>
                        <p>Hiding personal info and saving…</p>
                        <span className="processing-hint">This only takes a moment — keep this page open.</span>
                    </div>
                </div>
            )}

            {showSaveWithoutRedactionWarning && (
                <div className="privacy-warning-modal-backdrop" role="presentation">
                    <div className="privacy-warning-modal" role="dialog" aria-modal="true" aria-labelledby="privacy-warning-title">
                        <h2 id="privacy-warning-title">This might still show personal info</h2>
                        <p>
                            It looks like this {documentLabel} may still have personal details, and you haven’t covered anything.
                            You can still save it, but those details would be visible to others.
                        </p>
                        <div className="privacy-warning-signals">
                            {detectedPrivacySignals.map(signal => (
                                <span key={signal.id}>{signal.label}</span>
                            ))}
                        </div>
                        <div className="privacy-warning-actions">
                            <button type="button" className="secondary-btn" onClick={handleGoBackAndRedact}>Go back and cover it</button>
                            <button type="button" className="danger-btn" onClick={handleConfirmSaveWithoutRedactions}>Save anyway</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Step3Details;
